import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    createVideo,
    updateVideo,
    clearMessages
} from '../../../redux/slices/admin/videoSlice';

const VideoModal = ({ isOpen, closeModal, initialData, isEditMode }) => {
    const videoCategories = useSelector((state) => state.app_options?.video_categories_min);
    const dispatch = useDispatch();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        category: '1',
        sort_order: '',
        video_url: '',
        description: '',
        web_thumbnail: null,
        mobile_thumbnail: null,
        video_file:null,
        video_type: 'url',
    });

    const errorMessage = useSelector((state) => state.admin_videos?.error);

    useEffect(() => {
        if (isEditMode && initialData) {
            setFormData({
                title: initialData.title || '',
                category: initialData.category || '1',
                sort_order: initialData.sort_order || '',
                video_url: initialData.video_url || '',
                description: initialData.description || '',
                web_thumbnail: null,
                mobile_thumbnail: null,
                video_file:null,
                video_type: 'url',
            });
        }
    }, [isEditMode, initialData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setFormData({ ...formData, [name]: files[0] });
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();

        setIsSubmitting(true);
        setUploadProgress(0);

        const interval = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 90) return prev; // don't go beyond 90% until done
                return prev + 5;
            });
        }, 200);

        const videoFormData = new FormData();
        videoFormData.append('title', formData.title);
        videoFormData.append('category', formData.category);
        videoFormData.append('sort_order', formData.sort_order);
        videoFormData.append('video_url', formData.video_url);
        videoFormData.append('description', formData.description);
        videoFormData.append('video_type', formData.video_type);
        videoFormData.append('id', initialData.id);

        if (formData.web_thumbnail) {
            videoFormData.append('web_thumbnail', formData.web_thumbnail);
        }

        if (formData.mobile_thumbnail) {
            videoFormData.append('mobile_thumbnail', formData.mobile_thumbnail);
        }

        if (formData.video_file) {
            videoFormData.append('video_file', formData.video_file);
        }

        try {
            if (isEditMode) {
                await dispatch(updateVideo(videoFormData)).unwrap();
            } else {
                await dispatch(createVideo(videoFormData)).unwrap();
            }

            setFormData({
                title: '',
                category: '1',
                sort_order: '',
                video_url: '',
                description: '',
                web_thumbnail: null,
                mobile_thumbnail: null,
                video_type:'url',
                video_file:null,
            });

            setUploadProgress(100);
            setTimeout(() => {
                setIsSubmitting(false);
                setUploadProgress(0);
                handleClose();
            }, 500);
        } catch (error) {
            console.error('Failed to submit:', error);
            setIsSubmitting(false);
            setUploadProgress(0);
        } finally {
            clearInterval(interval);
        }
    };

    const handleClose = () => {
        dispatch(clearMessages()); // Clear messages only on modal close
        setFormData({
            title: '',
            category: '1',
            sort_order: '',
            video_url: '',
            description: '',
            web_thumbnail: null,
            mobile_thumbnail: null,
            video_file: null,
            video_type: 'url',
        });
        closeModal();
    };

    const fileInputRef1 = useRef(null);
    const fileInputRef2 = useRef(null);
    const fileInputRef3 = useRef(null);

    useEffect(() => {
      if (formData.web_thumbnail === null && fileInputRef1.current) {
        fileInputRef1.current.value = '';
      }
    }, [formData.web_thumbnail]);

    useEffect(() => {
      if (formData.mobile_thumbnail === null && fileInputRef2.current) {
        fileInputRef2.current.value = '';
      }
    }, [formData.mobile_thumbnail]);

    useEffect(() => {
      if (formData.video_file === null && fileInputRef3.current) {
        fileInputRef3.current.value = '';
      }
    }, [formData.video_file]);

    if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" id="modalVideo" tabIndex="-1" aria-labelledby="modalVideoLabel" aria-hidden="true" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-create">
        <div className="modal-content">
          <div className="modal-header justify-content-center">
            <div className="w-100">
              <h1 className="modal-title" id="modalVideoLabel">{isEditMode ? 'Edit Video' : 'New Video'}</h1>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
          </div>
          <div className="modal-body px-md-5 pb-md-4">
            <div className="row g-3 gx-md-5 gy-md-4">
              <div className="col-md-12">
                <input type="text" placeholder="Title" className="form-control form-control-lg" name="title" value={formData.title} onChange={handleInputChange} />
              </div>
              <div className="col-md-6">
                <select className="form-control form-control-lg" name="category" value={formData.category} onChange={handleInputChange}>
                  {videoCategories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <input type="number" placeholder="Sort Order" className="form-control form-control-lg" name="sort_order" value={formData.sort_order} onChange={handleInputChange} />
              </div>

            <div className="col-md-12" >
              <label className="form-label" style={{display:'none'}}>Video Source</label>
              <div className="d-flex gap-3 mb-2">
                <div className="form-check" style={{position: 'relative', top: 0, display:'none' }}>
                  <input
                    type="radio"
                    name="video_type"
                    value="url"
                    checked={formData.video_type === 'url'}
                    onChange={handleInputChange}
                    className="form-check-input"
                    id="radio-url"
                  />
                  <label className="form-check-label ms-2" htmlFor="radio-url">Video URL</label>
                </div>
              <div className="form-check" style={{ position: 'relative', top: 0, display:'none' }}>
                <input
                  type="radio"
                  name="video_type"
                  value="file"
                  checked={formData.video_type === 'file'}
                  onChange={handleInputChange}
                  className="form-check-input"
                  id="radio-file"
                />
                <label style={{display:'none'}} className="form-check-label ms-2" htmlFor="radio-file">Upload Video</label>
              </div>
            </div>

              {formData.video_type === 'url' ? (
                <input
                  type="text"
                  placeholder="Video URL"
                  className="form-control form-control-lg"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleInputChange}
                />
              ) : (
                <input
                  type="file"
                  accept="video/*"
                  className="form-control form-control-lg"
                  name="video_file"
                  ref={fileInputRef3}
                  onChange={handleFileChange}
                />
              )}
          </div>



              <div className="col-md-12">
                <textarea className="form-control form-control-lg" placeholder="Description" rows="3" name="description" value={formData.description} onChange={handleInputChange}></textarea>
              </div>

              <div className="col-md-6">
                <label>Web Thumbnail</label>
                <div className="position-relative">
                  <input type="file" ref={fileInputRef1} className="form-control form-control-lg form-control-choose" name="web_thumbnail" onChange={handleFileChange} />
                </div>
              </div>
              <div className="col-md-6">
                <label>Mobile Thumbnail</label>
                <div className="position-relative">
                  <input type="file" ref={fileInputRef2} className="form-control form-control-lg form-control-choose" name="mobile_thumbnail" onChange={handleFileChange} />
                </div>
              </div>
              <div className="col-md-12">
                <button type="button" className="btn btn-create w-100" onClick={handleFormSubmit}>
                  {isEditMode ? 'Save' : 'Create'}
                </button>
              </div>
              <div className="col-md-12">
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
              </div>

              {isSubmitting && (
                <div className="col-md-12" style={{display:'none'}}>
                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;